<script setup lang="ts">
import type { ITableFilterProps } from '@modules/tables/components/VTableFilter/types';
import type { ITableColumnsOptions } from '@modules/tables/types';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VDownloadReportButton from '@modules/download-report/components/VDownloadReportButton/VDownloadReportButton.vue';
import { useDownloadReport } from '@modules/download-report/composables';
import { ESmartFilterColumnType } from '@modules/smart-filters/enums';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';
import {
  TRANSLATION_GROUP_WITHDRAWALS_HISTORY,
  WITHDRAWALS_HISTORY_PERMISSIONS,
} from '@modules/withdrawals-history/constants';
import { useDownloadWithdrawalsHistoryReportMutation } from '@modules/withdrawals-history/mutations';
import { useWithdrawalsHistoryListQuery } from '@modules/withdrawals-history/queries';
import { ADMIN_WITHDRAWALS_HISTORY_VIEW_ROUTE_NAME } from '@modules/withdrawals-history/submodules/view/constants';

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useWithdrawalsHistoryListQuery);

const {
  isReportLoading,
  onDownloadReport,
} = useDownloadReport(useDownloadWithdrawalsHistoryReportMutation(), tableState);

const tableFilterOptions: ITableFilterProps = {
  pathForFilter: 'api2/admin/affiliate_cashouts/approved_and_declined_history',
  actionForFilter: 'list',
  byMerchant: true,
  filterName: 'approved_and_declined_history',
};

const columnOptions: ITableColumnsOptions = {
  merchant_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  created_at: {
    type: ESmartFilterColumnType.DATE,
  },
  updated_at: {
    type: ESmartFilterColumnType.DATE,
  },
  id: {
    type: ESmartFilterColumnType.COMPARE,
  },
  creator_id: {
    type: ESmartFilterColumnType.COMPARE,
  },
  creator_type: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  affiliate_id: {
    type: ESmartFilterColumnType.COMPARE,
    defaultMobilePreview: true,
  },
  affiliate_email: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  affiliate_username: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  amount: {
    type: ESmartFilterColumnType.COMPARE,
  },
  state: {
    type: ESmartFilterColumnType.CHECKBOX,
    localization: {
      translationPath: 'admin.partners.withdrawals_history.state.',
    },
  },
  admin_email: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  payment_system_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  payment_system_details: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
};
</script>

<template>
  <VCard v-data-qa-auto-id="'WithdrawalsHistoryListPage'" type="table">
    <VTable
      :data="data"
      :state="tableState"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="$getTPath(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'HEADER')"
      :filter-props="tableFilterOptions"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          icon="pi-eye"
          :to="{
            name: ADMIN_WITHDRAWALS_HISTORY_VIEW_ROUTE_NAME,
            params: {
              id: listItem.id,
            },
          }"
        />
      </template>
      <template
        v-if="$hasPermission(WITHDRAWALS_HISTORY_PERMISSIONS.DOWNLOAD_LIST)"
        #footerButtons
      >
        <VDownloadReportButton
          :label="$getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'DOWNLOAD_REPORT')"
          :loading="isReportLoading"
          @download="onDownloadReport"
        />
      </template>
    </VTable>
  </VCard>
</template>
